import { DomainError } from '@telespot/domain';

import { MethodMarks } from '../method-marks';
import { MarkedProxyDecorator } from '../proxy.decorator';
import { RetryBudget } from './retry-budget';
import { RetryCost, WeightedRetryCost } from './retry-cost';
import { ExponentialRetryStrategy, RetryStrategy } from './retry-strategy';

const marks = new MethodMarks<RetryOverrides>('shared-decorators:retryableMethods');

/**
 * Marks a method as safe to replay.
 *
 * The mark is a declaration, not behaviour: it records the method name on the
 * prototype and wraps nothing. Retrying is still applied by {@link Retrier}
 * at the composition root, which keeps ownership of how many attempts to make, how
 * long to wait, and which errors count as transient.
 *
 * Mark an operation only if replaying it is harmless — reads, and writes that are
 * idempotent for every caller. Marks are per class, so a method whose replay-safety
 * depends on the calling endpoint should be left unmarked.
 *
 * Applies to prototype methods only; an arrow-function class field cannot carry a
 * method decorator.
 *
 * @example
 * class S3FileRepository implements FileRepository {
 *   @retryable()
 *   public fetch(path: string): Promise<Buffer> { ... }
 *
 *   public write(path: string, content: Buffer): Promise<void> { ... }  // never replayed
 * }
 */
export function retryable(overrides?: RetryOverrides): MethodDecorator {
  return (prototype, propertyKey) => marks.add(prototype, propertyKey, overrides);
}

/**
 * The methods marked with {@link retryable} on `target` or anywhere up its prototype
 * chain, mapped to their overrides. Empty when nothing is marked.
 *
 * A delegating function rather than `= marks.of`: detaching a method from its instance
 * loses `this`, which would compile and then fail at runtime.
 */
export function retryableMethodsOf(target: object): ReadonlyMap<string, RetryOverrides | undefined> {
  return marks.of(target);
}

/** Decides whether a given error is worth another attempt. */
export interface RetryDecider<T extends Error = Error> {
  retryable(error: T): boolean;
}

/**
 * Default gate: defer to the domain's own judgement on whether an error is transient.
 * Anything that is not a {@link DomainError} is treated as non-retriable, so an
 * untranslated target never retries by accident.
 */
export const domainErrorRetryDecider: RetryDecider = {
  retryable: (error: Error): boolean => error instanceof DomainError && error.retriable,
};

/**
 * How a replay is performed. *Which* operations may be replayed is not part of this —
 * that is declared on the target with {@link retryable}.
 *
 * Retryability is three independent questions, answered in three different places:
 *
 * - *May I replay this operation?* — the {@link retryable} marks. A property of the
 *   operation, so it lives with the operation.
 * - *Is this error transient?* — the {@link RetryDecider} below. A property of the
 *   error, so it is decided by whoever classified it.
 * - *Can the downstream afford another attempt?* — the {@link RetryBudget}. A property
 *   of the dependency's health, so it is shared per downstream rather than per call.
 *
 * Keeping them apart means you never have to misclassify an error to stop an
 * operation from being retried: just don't mark it.
 */
export interface RetryPolicy {
  /** Total calls including the first. Must be >= 1; `1` disables retrying. */
  maxAttempts: number;
  /** Backoff between attempts. Defaults to {@link ExponentialRetryStrategy}. */
  retryStrategy?: RetryStrategy;
  /** Overrides {@link domainErrorRetryDecider} when retriability is not carried by the error. */
  decider?: RetryDecider;
  /**
   * Shared allowance every retry is drawn from. Unbudgeted when omitted: `maxAttempts`
   * then bounds each call and nothing bounds their sum. See {@link SuccessRatioRetryBudget}.
   */
  budget?: RetryBudget;
  /**
   * Prices a retry against the {@link RetryPolicy.budget}, so the failures that amplify
   * an outage exhaust it first. Defaults to {@link WeightedRetryCost}.
   */
  retryCost?: RetryCost;
}

/**
 * Per-method overrides, applied on top of the instance {@link RetryPolicy}.
 *
 * Derived from the policy with `Pick`, so the overridable subset is a property of the
 * type: `retryStrategy`, `decider`, `budget` and `retryCost` are excluded structurally,
 * because backoff shape and the budget are properties of the downstream, and the decider
 * and pricing properties of its errors — none of which varies by method.
 */
export type RetryOverrides = Pick<RetryPolicy, 'maxAttempts'>;

/**
 * Retrier
 *
 * A proxy-based decorator that replays a target's {@link retryable} methods when they
 * reject with an error the decider considers transient, waiting `policy.retryStrategy`
 * between attempts and giving up after `policy.maxAttempts` total calls.
 *
 * The attempt budget is per invocation, never pooled: this holds no mutable state, so one
 * instance decorating many targets cannot leak attempts between methods, targets or
 * concurrent calls.
 *
 * A {@link RetryPolicy.budget} is the deliberate exception, and pooling is the point of
 * it. Its state lives in the budget object rather than here, so a Retrier without one is
 * still stateless and two with different attempt limits can share one allowance.
 *
 * Which methods are eligible is read from the target, so one Retrier can be reused
 * across adapters and each is replayed according to its own marks. Unmarked methods are
 * returned untouched — the proxy adds no behaviour to them at all. Only rejected
 * Promises are retried: a method that returns synchronously, or throws synchronously,
 * passes straight through, so decorating a synchronous method never changes its
 * signature.
 *
 * This decorator does **not** translate errors. Decorate an already-translated target
 * so that the errors it inspects are {@link DomainError}s and the default decider
 * applies; each retry re-enters through the translating decorator, so every attempt is
 * translated too.
 *
 * @example
 * const translating = new ErrorTranslationDecorator(new S3ErrorTranslator());
 * const retrier = new Retrier({ maxAttempts: 3 });   // exponential backoff by default
 *
 * // Each target is replayed per its own @retryable() marks
 * const assetFiles = retrier.decorate(translating.decorate(assetRepository));
 * const maskFiles = retrier.decorate(translating.decorate(maskRepository));
 *
 * @example
 * // Budgeted: retries are bounded by what S3 is successfully serving, one budget per
 * // downstream and shared by every adapter that talks to it.
 * const s3Budget = new SuccessRatioRetryBudget({ retryRatio: 0.1, maxTokens: 20 });
 * const budgeted = new Retrier({ maxAttempts: 3, budget: s3Budget });
 */
export class Retrier extends MarkedProxyDecorator<RetryOverrides> {
  private readonly decider: RetryDecider;
  private readonly retryStrategy: RetryStrategy;
  private readonly budget: RetryBudget | undefined;
  private readonly retryCost: RetryCost;

  constructor(private readonly policy: RetryPolicy) {
    super(marks);

    // An attempt limit below one call is nonsensical, and NaN would compare false against
    // every bound and retry forever. Fail at construction instead.
    if (!(policy.maxAttempts >= 1)) throw new Error('[Retrier] maxAttempts must be at least 1');

    this.decider = policy.decider ?? domainErrorRetryDecider;
    this.retryStrategy = policy.retryStrategy ?? new ExponentialRetryStrategy();
    this.budget = policy.budget;
    this.retryCost = policy.retryCost ?? new WeightedRetryCost();
  }

  protected wrapMarked(
    method: (...args: unknown[]) => unknown,
    _: string,
    overrides: RetryOverrides | undefined
  ): (...args: unknown[]) => unknown {
    const maxAttempts = overrides?.maxAttempts ?? this.policy.maxAttempts;

    return (...args: unknown[]) => {
      const result = method(...args);

      return result instanceof Promise ? this.retry(method, args, result, maxAttempts) : result;
    };
  }

  private async retry(
    method: (...args: unknown[]) => unknown,
    args: unknown[],
    firstAttempt: Promise<unknown>,
    maxAttempts: number
  ): Promise<unknown> {
    // Local to this invocation, so the attempt count is never shared with another call.
    let pending: Promise<unknown> = firstAttempt;

    for (let attempt = 0; ; attempt++) {
      try {
        const value = await pending;

        // One deposit per call the downstream served, not per attempt it survived.
        this.budget?.deposit();

        return value;
      } catch (error) {
        if (!this.decider.retryable(error as Error) || attempt >= maxAttempts - 1) throw error;

        // Charged last, so the budget only pays for retries that were going to happen.
        if (!this.charge(error as Error)) throw error;

        await this.delay(attempt);
        // Wrap in an async IIFE so a synchronous throw on re-invocation surfaces as a
        // rejection on the next iteration's `await` rather than escaping the loop.
        pending = (async () => method(...args))();
      }
    }
  }

  /** Whether the budget could pay for one retry of `error`. True when unbudgeted. */
  private charge(error: Error): boolean {
    if (!this.budget) return true;

    return this.budget.tryWithdraw(this.retryCost.of(error));
  }

  private delay(attempt: number): Promise<void> {
    const ms = this.retryStrategy.backoff(attempt);

    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
